import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../../../core/components/Button';
import { RemoteImage } from '../../../core/components/RemoteImage';
import { useSettings } from '../../../core/api/hooks/useSettings';
import HesnLogo from '../../../assets/logo.png';

export const HeroSection = () => {
    const { t } = useTranslation();
    const { data: settings } = useSettings();

    // Build WhatsApp deep-link — strip non-digit chars but keep leading +
    const whatsappHref = settings?.whatsapp
        ? `https://wa.me/${settings.whatsapp.replace(/[^\d+]/g, '').replace('+', '')}`
        : '#';

    return (
        <section className="relative h-[65svh] sm:min-h-[100svh] w-full overflow-hidden flex items-center justify-center">
            {/* Video Background Layer */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-secondary/90 z-10" />
                <div className="w-full h-full bg-slate-900">
                    <RemoteImage
                        src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop"
                        alt="Architectural Skeleton"
                        className="w-full h-full object-cover opacity-60 grayscale scale-110"
                    />
                </div>
            </div>

            {/* Content Layer */}
            <div className="relative z-20 w-full px-4 sm:px-6 text-center text-white flex flex-col items-center justify-center h-full">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="flex justify-center mb-8 sm:mb-12 relative z-10 w-full"
                >
                    <img
                        src={HesnLogo}
                        alt="HESN Development"
                        className="h-20 sm:h-28 md:h-36 w-auto object-contain brightness-0 invert"
                    />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="relative z-20 mt-4 sm:mt-0"
                >
                    <p className="text-sm sm:text-lg md:text-2xl font-light tracking-wide text-slate-200 mb-2 sm:mb-4 max-w-3xl mx-auto px-4">
                        {t('hero.tagline')}
                        <span className="block sm:inline"> </span>
                        <span className="text-slate-400 text-xs sm:text-base md:text-lg">{t('hero.subtitle')}</span>
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full sm:w-auto">
                        <Link to="/explore" className="w-full sm:w-auto">
                            <Button size="lg" className="bg-white text-secondary hover:bg-slate-200 w-full text-sm sm:text-base py-3 sm:py-3 h-auto font-bold">
                                {t('hero.explorePortfolio')}
                            </Button>
                        </Link>
                        <a
                            href={whatsappHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full sm:w-auto"
                        >
                            <Button
                                variant="outline"
                                size="lg"
                                className="border-white text-white hover:bg-white hover:text-secondary w-full text-sm sm:text-base py-3 sm:py-3 h-auto font-bold"
                                disabled={!settings?.whatsapp}
                            >
                                {t('hero.consultExpert')}
                            </Button>
                        </a>
                    </div>
                </motion.div>
            </div>

            {/* Scroll Indicator - Hidden on mobile */}
            <motion.div
                className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 z-20 hidden sm:block"
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
            >
                <div className="w-[1px] h-12 sm:h-16 bg-gradient-to-b from-white to-transparent opacity-50" />
            </motion.div>
        </section>
    );
};
